import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CLEF_PATH from '@/constants/path';
import { ClientFeatures, useFeatureGateEnabled } from '@/hooks/useFeatureGate';
import CameraIcon from '@/images/home/Camera.svg';
import CloudIcon from '@/images/home/Cloud.svg';
import { Box, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import { useHistory } from 'react-router';
import IntroVideo from './IntroVideo';

const CARD_CONTENT_HEIGHT = 140;

const useStyles = makeStyles(theme => ({
  introSectionContainer: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
    width: '100%',
    maxWidth: 1604,
    padding: theme.spacing(0, 8),
  },
  cardTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'start',
    minHeight: 40,
    gap: theme.spacing(3),
  },
  quickDemoCardContainer: {
    display: 'flex',
    borderRadius: 10,
    width: '66.7%',
    marginBottom: theme.spacing(10),
    maxWidth: 800,
  },
  quickDemoCardVideo: {
    display: 'flex',
    height: CARD_CONTENT_HEIGHT,
    width: 'auto',
    overflow: 'hidden',
    borderRadius: 10,
    position: 'relative',
    boxShadow: '0px 40px 80px -20px rgba(0, 66, 218, 0.08)',
  },
  quickDemoCardDescriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minWidth: 220,
  },
  quickDemoCardDescriptionTitle: {
    marginBottom: theme.spacing(5),
    lineHeight: '24px',
  },
  examplesCardContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    padding: theme.spacing(4),
    backgroundColor: `${theme.palette.greyModern[200]}`,
    border: `1px solid ${theme.palette.greyModern[300]}`,
    borderRadius: 10,
    marginBottom: theme.spacing(10),
    overflow: 'hidden',
    transition: 'scale 0.25s',
    cursor: 'pointer',
  },
  examplesCardImg: {
    height: CARD_CONTENT_HEIGHT,
    width: 'auto',
  },
  examplesCardTitle: {
    fontWeight: 500,
  },
  examplesCardLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.blue[700],
    fontWeight: 500,
  },
}));

export const IntroSection: React.FC = () => {
  const styles = useStyles();
  const history = useHistory();
  const showExamplesPage = !useFeatureGateEnabled(ClientFeatures.DisableExamplesPage);
  const enableSnowflakeIntroVideo = useFeatureGateEnabled(ClientFeatures.SnowflakeIntroVideo);
  const videoUrl = enableSnowflakeIntroVideo
    ? 'https://landing-lens-support.s3.us-east-2.amazonaws.com/video/snowflake_native_app_2mins.mp4'
    : 'https://landing-lens-support.s3.us-east-2.amazonaws.com/video/FRE+video_1920.mp4';
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  return (
    <Box className={styles.introSectionContainer}>
      <Box className={styles.examplesCardContainer}>
        <IntroVideo src={videoUrl} className={styles.quickDemoCardVideo} videoRef={videoRef} />

        <Box className={styles.cardTextContainer}>
          <Typography variant="h2" className={styles.examplesCardTitle}>
            {t('LandingLens Workflow')}
          </Typography>
          <Typography>
            {t('Get started with LandingLens with this 2-minute walkthrough.')}
          </Typography>
          <Typography
            className={styles.examplesCardLink}
            onClick={() => {
              if (videoRef.current) {
                videoRef.current.requestFullscreen?.();
                videoRef.current.play();
              }
            }}
          >
            {t('Watch Video')}
          </Typography>
        </Box>
      </Box>

      {showExamplesPage && (
        <Box
          className={styles.examplesCardContainer}
          onClick={() => history.push(CLEF_PATH.examples)}
        >
          <img src={CameraIcon} className={styles.examplesCardImg} />

          <Box className={styles.cardTextContainer}>
            <Typography variant="h2" className={styles.examplesCardTitle}>
              {t('Image Preparation')}
            </Typography>
            <Typography>
              {t(
                'LandingLens works best with high-quality data. Learn image capture best practices.',
              )}
            </Typography>
            <Typography className={styles.examplesCardLink} onClick={e => e.stopPropagation()}>
              <a
                href="https://landinglens.docs.landing.ai/image-best-practices"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('View Guide')}
              </a>
              <OpenInNewIcon fontSize="small" />
            </Typography>
          </Box>
        </Box>
      )}

      {showExamplesPage && (
        <Box className={styles.examplesCardContainer}>
          <img src={CloudIcon} className={styles.examplesCardImg} />

          <Box className={styles.cardTextContainer}>
            <Typography variant="h2" className={styles.examplesCardTitle}>
              {t('Work with API')}
            </Typography>
            <Typography>
              {t(
                'Explore developer tools to speed up tasks like uploading, predicting, and post-processing.',
              )}
            </Typography>
            <Typography className={styles.examplesCardLink}>
              <a
                href="https://landinglens.docs.landing.ai/sdk"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('View Guide')}
              </a>
              <OpenInNewIcon fontSize="small" />
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
